import React, { createContext, useEffect } from "react";
import { SystemManagedDataCollection } from "@lifeat/shared/data-models/system/SystemManagedDataEntity";
import { CalendarVersion, parseVersion, versionLessThan } from "@lifeat/shared/system/CalendarVersion";
import LifeAtToastV2 from "@common/LifeAtToastV2/LifeAtToastV2";
import useDbValueListener from "@hooks/useDbValueListener";
import { reloadPage } from "@util/BrowserUtil";

const BEHIND_TOAST_TITLE = "Update Available";
const BEHIND_TOAST_DESCRIPTION = "There is a new version of LifeAt available. Please refresh the app to update.";
const BEHIND_TOAST_ACTION = "Refresh";
const UNSUPPORTED_TOAST_TITLE = "Refresh Required";
const UNSUPPORTED_TOAST_DESCRIPTION = "Please update to the latest version of LifeAt to continue using the app.";
const UNSUPPORTED_TOAST_ACTION = "Refresh Now";
const TOAST_DURATION_MS = 1000000;
const FORCE_REFRESH_TIMEOUT_MS = 5 * 60 * 1000;

type VersionContext = {
    loading: boolean;
    version?: CalendarVersion | null | undefined;
};

export const VersionContext = createContext<VersionContext>({
    loading: true,
    version: undefined,
});

type VersionContextProviderProps = {
    children?: React.ReactNode;
};

export function VersionContextProvider({ children }: VersionContextProviderProps) {
    const serverVersionString = useDbValueListener(SystemManagedDataCollection.at("appVersion").node);
    const serverVersion = serverVersionString ? parseVersion(serverVersionString) : null;
    const supportedVersionString = useDbValueListener(SystemManagedDataCollection.at("supportedVersion").node);
    const supportedVersion = supportedVersionString ? parseVersion(supportedVersionString) : null;
    const clientVersionString = process.env.LIFEAT_VERSION;
    const clientVersion = clientVersionString ? parseVersion(clientVersionString) : null;
    const loading = serverVersion == null;
    const versionStatus = calculateVersionStatus(clientVersion, serverVersion, supportedVersion);

    // Force refresh the page after timeout if the version is unsupported
    useEffect(() => {
        if (versionStatus === "unsupported") {
            const timeout = setTimeout(() => {
                reloadPage();
            }, FORCE_REFRESH_TIMEOUT_MS);

            return () => {
                clearTimeout(timeout);
            };
        }
        return undefined;
    }, [versionStatus]);

    return (
        <VersionContext.Provider value={{ loading, version: serverVersion }}>
            {children}
            {versionStatus === "behind" && (
                <LifeAtToastV2
                    root={{
                        type: "background",
                        duration: TOAST_DURATION_MS,
                    }}
                    title={BEHIND_TOAST_TITLE}
                    description={BEHIND_TOAST_DESCRIPTION}
                    action={{ content: BEHIND_TOAST_ACTION, altText: BEHIND_TOAST_ACTION, onClick: () => reloadPage() }}
                />
            )}
            {versionStatus === "unsupported" && (
                <LifeAtToastV2
                    root={{
                        type: "background",
                        duration: TOAST_DURATION_MS,
                    }}
                    title={UNSUPPORTED_TOAST_TITLE}
                    description={UNSUPPORTED_TOAST_DESCRIPTION}
                    action={{
                        content: UNSUPPORTED_TOAST_ACTION,
                        altText: UNSUPPORTED_TOAST_ACTION,
                        onClick: () => reloadPage(),
                    }}
                />
            )}
        </VersionContext.Provider>
    );
}

export function useVersionContext() {
    const context = React.useContext(VersionContext);
    if (context == null) {
        throw new Error("Missing version context.");
    }
    return context;
}

export type VersionStatus = "latest" | "behind" | "unsupported";

export function calculateVersionStatus(
    clientVersion: CalendarVersion | null | undefined,
    serverVersion: CalendarVersion | null | undefined,
    supportedVersion: CalendarVersion | null | undefined,
): VersionStatus {
    if (clientVersion == null || serverVersion == null) {
        return "latest";
    }
    if (!versionLessThan(clientVersion, serverVersion)) {
        return "latest";
    }
    if (supportedVersion != null && versionLessThan(clientVersion, supportedVersion)) {
        return "unsupported";
    }
    return "behind";
}
